import { Link } from "react-router-dom";
import type { ColumnDef } from "./types";
import type {
  MovieInCommon,
  MFLCatalogueFilm,
  MFLLeaderboardEntry,
} from "../../types";
import type { SwapFilm } from "../../../shared/types";
import StarRating from "../StarRating";
import { compareNullable, formatReleaseDate } from "../../utilities";
import { BoxOfficeDot } from "../MovieFantasyLeague/BoxOfficeDot";

export interface SwapFilmHeaderCtx {
  rater: string;
}

export const swapFilmColumns: ColumnDef<SwapFilm, SwapFilmHeaderCtx>[] = [
  {
    key: "title",
    label: "Title",
    sortKey: "title",
    customSort: (a: SwapFilm, b: SwapFilm) => a.title.localeCompare(b.title),
    renderColumn: (data: SwapFilm) => (
      <a
        href={`https://letterboxd.com/film/${data.film_slug}`}
        target="_blank"
        rel="noreferrer"
      >
        {data.title}
      </a>
    ),
  },
  {
    key: "user_rating",
    label: "Rating",
    sortKey: "user_rating",
    // Ascending, nulls lowest; equal/both-null return 0 so the stable sort keeps
    // the server's title-ASC secondary order (alphabetical ties both directions).
    customSort: (a: SwapFilm, b: SwapFilm) => {
      if (a.user_rating === b.user_rating) return 0;
      if (a.user_rating === null) return -1;
      if (b.user_rating === null) return 1;
      return a.user_rating - b.user_rating;
    },
    renderColumn: (data: SwapFilm) =>
      data.user_rating === null ? (
        <span className="text-letterboxd-text-muted italic">not rated</span>
      ) : (
        <StarRating rating={data.user_rating} />
      ),
  },
];

export interface MoviesInCommonHeaderCtx {
  user1: string;
  user2: string;
}

export const moviesInCommonColumns: ColumnDef<
  MovieInCommon,
  MoviesInCommonHeaderCtx
>[] = [
  {
    key: "title",
    label: "Film title",
    sortKey: "title",
  },
  {
    key: "user1_rating",
    label: "User 1 rating",
    customLabel: (ctx) => ctx?.user1 ?? "User 1 rating",
    sortKey: "user1_rating",
    renderColumn: (data: MovieInCommon) => (
      <StarRating rating={data.user1_rating} />
    ),
  },
  {
    key: "user2_rating",
    label: "User 2 rating",
    customLabel: (ctx) => ctx?.user2 ?? "User 2 rating",
    sortKey: "user2_rating",
    renderColumn: (data: MovieInCommon) => (
      <StarRating rating={data.user2_rating} />
    ),
  },
];

// aria-label expands the abbreviation; without it a screen reader spells or
// mangles "TBA".
const TBA = (
  <span className="text-letterboxd-text-muted" aria-label="to be announced">
    TBA
  </span>
);

const renderReleaseDate = (releaseDate: string) => (
  <span className="flex items-center gap-2 text-letterboxd-text-muted">
    {formatReleaseDate(releaseDate)}
    <BoxOfficeDot releaseDate={releaseDate} />
  </span>
);

/** The /mfl standings: server-ranked rosters, ties sharing a rank. */
export const mflLeaderboardColumns: ColumnDef<MFLLeaderboardEntry>[] = [
  {
    key: "rank",
    label: "#",
    sortKey: "rank",
    customSort: (a, b) => a.rank - b.rank,
  },
  {
    key: "roster",
    label: "Roster",
    sortKey: "roster",
    customSort: (a, b) => a.name.localeCompare(b.name),
    renderColumn: (entry) => (
      <>
        <Link to={`/mfl/roster/${entry.rosterId}`}>{entry.name}</Link>{" "}
        <span className="text-letterboxd-text-secondary">
          ({entry.lbusername})
        </span>
      </>
    ),
  },
  {
    key: "totalPoints",
    label: "Points",
    sortKey: "totalPoints",
    customSort: (a, b) => a.totalPoints - b.totalPoints,
  },
];

/** The /mfl summary: four columns, no per-category breakdown. */
export const mflFilmSummaryColumns: ColumnDef<MFLCatalogueFilm>[] = [
  {
    key: "title",
    label: "Film",
    sortKey: "title",
    customSort: (a, b) => a.title.localeCompare(b.title),
    renderColumn: (film) => (
      <Link to={`/mfl/film/${film.filmSlug}`}>{film.title}</Link>
    ),
  },
  {
    key: "releaseDate",
    label: "Released",
    sortKey: "releaseDate",
    customSort: (a, b) => compareNullable(a.releaseDate, b.releaseDate),
    renderColumn: (film) =>
      film.releaseDate === null
        ? TBA
        : renderReleaseDate(film.releaseDate),
  },
  {
    key: "price",
    label: "Price",
    sortKey: "price",
    customSort: (a, b) => compareNullable(a.price, b.price),
    renderColumn: (film) => (film.price === null ? TBA : `$${film.price}`),
  },
  {
    key: "totalPoints",
    label: "Points",
    sortKey: "totalPoints",
    customSort: (a, b) => a.totalPoints - b.totalPoints,
  },
];
